import React, { useState } from 'react';
import { useAchievements, ACHIEVEMENTS } from '@/hooks/useAchievements';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Trophy, Lock, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Mini badge display for dashboard header
 */
export const AchievementMini = ({ userId }) => {
  const { unlockedCount, totalCount, achievements } = useAchievements(userId);
  const recentThree = achievements.slice(-3).reverse();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="flex items-center gap-2 bg-purple-900/30 hover:bg-purple-900/50 border border-purple-500/30 rounded-lg px-3 py-2 transition-all">
          <Trophy className="w-4 h-4 text-purple-400" />
          <span className="text-purple-300 text-sm font-medium">{unlockedCount}/{totalCount}</span>
          <div className="flex -space-x-1">
            {recentThree.map((a) => (
              <span key={a.id} className="text-lg" title={a.name}>{a.icon}</span>
            ))}
          </div>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-purple-500/30 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-purple-300 flex items-center gap-2">
            <Trophy className="w-6 h-6" />
            I Tuoi Trofei della Famiglia
          </DialogTitle>
        </DialogHeader>
        <AchievementGrid userId={userId} />
      </DialogContent>
    </Dialog>
  );
};

/**
 * Full achievement grid
 */
export const AchievementGrid = ({ userId }) => {
  const { achievements, allAchievements, progress, stats } = useAchievements(userId);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Tutti', icon: '🏆' },
    { id: 'time', name: 'Tempo', icon: '⏰' },
    { id: 'tasks', name: 'Task', icon: '✅' },
    { id: 'negotiations', name: 'Negoziati', icon: '🤝' },
    { id: 'rispetto', name: 'Rispetto', icon: '👑' },
    { id: 'special', name: 'Speciali', icon: '⭐' },
  ];

  const unlockedIds = new Set(achievements.map(a => a.id));

  const filteredAchievements = selectedCategory === 'all'
    ? allAchievements
    : allAchievements.filter(a => a.category === selectedCategory);

  return (
    <div className="space-y-4">
      {/* Progress bar */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-purple-300 text-sm">Progresso Totale</span>
          <span className="text-purple-400 font-bold">{achievements.length}/{allAchievements.length}</span>
        </div>
        <Progress value={progress} className="h-2 bg-slate-700" />
        <p className="text-slate-400 text-xs mt-2">
          {progress < 25 && "Sei appena entrato nella Famiglia... 🤌"}
          {progress >= 25 && progress < 50 && "Stai diventando un vero soldato! 💪"}
          {progress >= 50 && progress < 75 && "Il Don inizia a notarti... 👀"}
          {progress >= 75 && progress < 100 && "Quasi una leggenda! 🌟"}
          {progress === 100 && "SEI LA LEGGENDA DELLA FAMIGLIA! 👑🎉"}
        </p>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              selectedCategory === cat.id
                ? 'bg-purple-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {cat.icon} {cat.name}
          </button>
        ))}
      </div>

      {/* Achievement grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {filteredAchievements.map((achievement) => {
          const isUnlocked = unlockedIds.has(achievement.id);

          return (
            <div
              key={achievement.id}
              className={`relative p-4 rounded-xl border transition-all ${
                isUnlocked
                  ? 'bg-gradient-to-br from-purple-900/40 to-amber-900/20 border-purple-500/40 hover:border-purple-400/60'
                  : 'bg-slate-800/30 border-slate-700/30 opacity-60'
              }`}
            >
              {/* Icon */}
              <div className={`text-4xl mb-2 ${!isUnlocked && 'grayscale opacity-40'}`}>
                {isUnlocked ? achievement.icon : '🔒'}
              </div>

              {/* Name */}
              <h3 className={`font-bold text-sm ${isUnlocked ? 'text-purple-200' : 'text-slate-500'}`}>
                {achievement.name}
              </h3>

              {/* Description */}
              <p className={`text-xs mt-1 ${isUnlocked ? 'text-slate-400' : 'text-slate-600'}`}>
                {achievement.description}
              </p>

              {/* Unlocked indicator */}
              {isUnlocked && (
                <div className="absolute top-2 right-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Stats preview */}
      {stats && (
        <div className="bg-slate-800/30 rounded-lg p-4 mt-4">
          <h4 className="text-slate-400 text-sm mb-3 font-medium">📊 Le Tue Statistiche</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div>
              <p className="text-2xl font-bold text-amber-400">{Math.round(stats.totalHours || 0)}</p>
              <p className="text-xs text-slate-500">Ore Totali</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-400">{stats.completedTasks || 0}</p>
              <p className="text-xs text-slate-500">Task Completati</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-400">{stats.currentStreak || 0}</p>
              <p className="text-xs text-slate-500">Giorni Streak</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-400">{stats.wonNegotiations || 0}</p>
              <p className="text-xs text-slate-500">Negoziati Vinti</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * New achievement celebration popup
 */
export const AchievementPopup = ({ achievement, onDismiss }) => {
  if (!achievement) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 animate-in fade-in">
      <div className="relative bg-gradient-to-br from-purple-900 to-amber-900 rounded-2xl p-8 max-w-sm mx-4 text-center animate-in zoom-in-95 border-2 border-amber-500/50 shadow-2xl shadow-purple-500/20">
        {/* Close button */}
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 text-white/50 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Sparkles animation */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-ping"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${1 + Math.random()}s`,
              }}
            >
              ✨
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="relative">
          <p className="text-amber-300 text-sm font-medium mb-2 uppercase tracking-wider">
            🎉 Nuovo Trofeo Sbloccato! 🎉
          </p>

          <div className="text-7xl my-4 animate-bounce">
            {achievement.icon}
          </div>

          <h2 className="text-2xl font-black text-white mb-2">
            {achievement.name}
          </h2>

          <p className="text-purple-200 text-sm mb-4">
            {achievement.description}
          </p>

          <Button
            onClick={onDismiss}
            className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-6"
          >
            🤌 Magnifico!
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AchievementMini;
